import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ROUTES } from "../../../api/ApiRoutes";
import { getAuthedInstance } from "./useCollectionGames";

export type AvailableProvider = {
  provider_id: string;
  name: string;
  slug?: string;
  identifier?: string;
  is_active?: boolean;
};

export type AvailableProvidersMeta = {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
};

export type AvailableProvidersResult = {
  items: AvailableProvider[];
  total: number;
  meta?: AvailableProvidersMeta;
};

export type CollectionProvider = {
  provider_id: string;
  identifier?: string;
  name: string;
  is_active?: boolean;
  is_pinned?: boolean;
  display_order?: number | null;
  game_count?: number;
  icon_url?: string;
  tags?: {
    tag_id: string;
    name: string;
    name_translations?: Record<string, string>;
    color?: string;
    is_seed?: boolean;
  }[];
};

export type CollectionProvidersResult = {
  items: CollectionProvider[];
  total: number;
  meta?: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
};

export async function fetchCollectionProviders(
  code: string,
  view: "all" | "popular" = "popular",
  page = 1,
  pageSize = 50,
  search?: string,
): Promise<CollectionProvidersResult> {
  const instance = getAuthedInstance();
  const params: Record<string, unknown> = { view, page, page_size: pageSize };
  if (search) params.search = search;

  const url = API_ROUTES.GET_COLLECTION_PROVIDERS.replace("{code}", encodeURIComponent(code));
  const { data: body } = await instance.get(url, { params });

  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch collection providers");
  }

  const data = body?.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.total ?? data?.meta?.total ?? items.length;

  return { items, total, meta: data?.meta };
}

export function useCollectionProviders(
  code: string | null,
  view: "all" | "popular" = "popular",
  search?: string,
  pageSize = 200,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "collections",
      code,
      "providers",
      view,
      search || "",
    ],
    queryFn: () => fetchCollectionProviders(code!, view, 1, pageSize, search),
    enabled: Boolean(code),
  });
}

export async function fetchAvailableProviders(
  excludeCollectionCode: string,
  page = 1,
  pageSize = 50,
  search?: string,
): Promise<AvailableProvidersResult> {
  const instance = getAuthedInstance();
  const params: Record<string, unknown> = {
    page,
    page_size: pageSize,
    exclude_collection_code: excludeCollectionCode,
  };
  if (search) params.search = search;

  const { data: body } = await instance.get(API_ROUTES.GET_ADMIN_PROVIDERS, {
    params,
  });

  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch available providers");
  }

  const data = body?.data;
  const rawItems = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.providers)
      ? data.providers
      : Array.isArray(data)
        ? data
        : [];
  const items: AvailableProvider[] = rawItems.map(
    (item: Record<string, unknown>) => ({
      provider_id: String(
        item.provider_id ?? item.identifier ?? item.slug ?? "",
      ),
      name: String(item.name ?? item.provider_id ?? ""),
      slug: item.slug ? String(item.slug) : undefined,
      identifier: item.identifier ? String(item.identifier) : undefined,
      is_active: item.is_active as boolean | undefined,
    }),
  );
  const metaRecord = data?.meta as Record<string, unknown> | undefined;
  const total =
    (data?.total as number) ??
    (metaRecord?.total as number) ??
    items.length;
  const pageNum = (metaRecord?.page as number) ?? page;
  const pageSizeNum = (metaRecord?.page_size as number) ?? pageSize;
  const totalPages =
    (metaRecord?.total_pages as number) ??
    Math.max(1, Math.ceil(total / pageSizeNum));
  const meta: AvailableProvidersMeta = {
    page: pageNum,
    page_size: pageSizeNum,
    total,
    total_pages: totalPages,
    has_next: Boolean(metaRecord?.has_next),
    has_prev: Boolean(metaRecord?.has_prev),
  };
  return { items, total, meta };
}

export function useAvailableProviders(
  excludeCollectionCode: string | null,
  search?: string,
  page = 1,
  pageSize = 50,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "availableProviders",
      excludeCollectionCode,
      search || "",
      page,
      pageSize,
    ],
    queryFn: () =>
      fetchAvailableProviders(excludeCollectionCode!, page, pageSize, search),
    enabled: Boolean(excludeCollectionCode),
  });
}

export function useAddCollectionProviders(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (providerIds: string[]) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.GET_COLLECTION_PROVIDERS.replace(
        "{code}",
        encodeURIComponent(collectionCode),
      );
      const { data } = await instance.post(url, { provider_ids: providerIds });
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to add providers");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "providers",
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "availableProviders"],
      });
    },
  });
}

export function useRemoveCollectionProvider(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ identifier }: { identifier: string }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.DELETE_COLLECTION_PROVIDER
        .replace("{code}", encodeURIComponent(collectionCode))
        .replace("{identifier}", encodeURIComponent(identifier));
      const { data } = await instance.delete(url);
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to remove provider");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "providers",
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: ["casinoConfiguration", "collections"],
      });
    },
  });
}

export function useMoveCollectionProvider(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      provider_id: string;
      target_position: number;
    }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.MOVE_COLLECTION_PROVIDER.replace(
        "{code}",
        encodeURIComponent(collectionCode),
      );
      const { data } = await instance.post(url, params);
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to move provider");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "providers",
        ],
      });
    },
  });
}

export type ProviderItem = {
  game_id: string;
  game_provider: string;
  name: string;
  is_active?: boolean;
  is_pinned?: boolean;
  display_order?: number | null;
};

export async function fetchProviderItems(
  collectionCode: string,
  providerId: string,
  page = 1,
  pageSize = 200,
  search?: string,
): Promise<{ items: ProviderItem[]; total: number }> {
  const instance = getAuthedInstance();
  const url = API_ROUTES.GET_COLLECTION_PROVIDER_ITEMS
    .replace("{code}", encodeURIComponent(collectionCode))
    .replace("{provider_id}", encodeURIComponent(providerId));
  const params: Record<string, unknown> = { page, page_size: pageSize };
  if (search) params.search = search;

  const { data: body } = await instance.get(url, { params });
  if (body?.success === false) {
    throw new Error(body?.message || "Failed to fetch provider items");
  }
  const data = body?.data;
  const items = Array.isArray(data?.items) ? data.items : [];
  const total = data?.total ?? data?.meta?.total ?? items.length;
  return { items, total };
}

export function useProviderItems(
  collectionCode: string | null,
  providerId: string | null,
  search?: string,
) {
  return useQuery({
    queryKey: [
      "casinoConfiguration",
      "collections",
      collectionCode,
      "providers",
      providerId,
      "items",
      search || "",
    ],
    queryFn: () => fetchProviderItems(collectionCode!, providerId!, 1, 200, search),
    enabled: Boolean(collectionCode) && Boolean(providerId),
  });
}

export function useMoveProviderItem(collectionCode: string, providerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      game_id: string;
      game_provider: string;
      target_position: number;
    }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.MOVE_COLLECTION_PROVIDER_ITEM
        .replace("{code}", encodeURIComponent(collectionCode))
        .replace("{provider_id}", encodeURIComponent(providerId));
      const { data } = await instance.post(url, params);
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to move item");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "providers",
          providerId,
          "items",
        ],
      });
    },
  });
}

export function useSetProviderTags(collectionCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      providerId,
      tagIds,
    }: {
      providerId: string;
      tagIds: string[];
    }) => {
      const instance = getAuthedInstance();
      const url = API_ROUTES.SET_PROVIDER_TAGS.replace(
        "{provider_id}",
        encodeURIComponent(providerId),
      );
      const { data } = await instance.post(url, { tag_ids: tagIds });
      if (data?.success === false) {
        throw new Error(data?.message || "Failed to set tags");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          "casinoConfiguration",
          "collections",
          collectionCode,
          "providers",
        ],
      });
    },
  });
}
